import { Layout } from '@/components/ui/layout';
import { PrivateAdminRoute } from '@/components/organisms/route/admin';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '@/hooks/useAlert';
import { Alert } from '@/components/molecules/alert';
import { Button, ButtonLoading } from '@/components/ui/button';
import { FileUpload } from '@/components/molecules/fileUpload';
import { Input, InputWithLabel } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { QUERY_PROJECT_ANALYSIS_REPORT_FOR_ADMIN } from '@/graphql/queries/projectAnalysis';
import { QUERY_USER_PROFILES, QUERY_USER_PROFILES_PAGE } from '@/graphql/queries/users';
import {
  MUTATION_CREATE_PROJECT_ANALYSIS_REPORT_DRAFT,
  MUTATION_PUBLISH_PROJECT_ANALYSIS_REPORT,
  MUTATION_SET_PROJECT_ANALYSIS_REPORT_RECIPIENTS,
} from '@/graphql/mutation/projectAnalysis';
import { uploadProjectAnalysisReportHtmlMultipart } from '@/services/project-analysis/uploadProjectAnalysisReportHtmlMultipart';
import { downloadProjectAnalysisReportHtmlFile } from '@/services/project-analysis/downloadProjectAnalysisReportHtml';
import { useAuth } from '@/hooks/useAuth';
import type {
  ProjectAnalysisReportAdminDetail,
  ProjectAnalysisReportStatus,
} from '@/graphql/types/projectAnalysis';
import type { UserProfile, UserProfilesPageResponse } from '@/graphql/types/admin';
import { AppRouters } from '@/utils/constants/routers';
import { dateFormat } from '@/utils/functions/dateFormat';
import { cn } from '@/lib/utils';
import { ReportHtmlPreview } from './ReportHtmlPreview';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, ChevronRight, Maximize2, Minimize2, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

function ReportStatusTag({ status }: { status: ProjectAnalysisReportStatus }) {
  const map: Record<ProjectAnalysisReportStatus, { label: string; className: string }> = {
    DRAFT: { label: 'Rascunho', className: 'bg-slate-200 text-slate-800' },
    PUBLISHED: { label: 'Publicado', className: 'bg-green-600 text-white' },
    ARCHIVED: { label: 'Arquivado', className: 'bg-slate-500 text-white' },
  };
  const { label, className } = map[status];
  return (
    <span className={cn('shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold', className)}>
      {label}
    </span>
  );
}

const DEFAULT_PAGE_SIZE = 15;

function requestFullscreenElement(el: HTMLElement): Promise<void> {
  if (el.requestFullscreen) {
    return el.requestFullscreen();
  }
  const w = el as HTMLElement & {
    webkitRequestFullscreen?: () => void;
    msRequestFullscreen?: () => void;
  };
  if (w.webkitRequestFullscreen) {
    w.webkitRequestFullscreen();
    return Promise.resolve();
  }
  if (w.msRequestFullscreen) {
    w.msRequestFullscreen();
    return Promise.resolve();
  }
  return Promise.reject(new Error('Fullscreen API not available'));
}

function exitFullscreenDocument(): Promise<void> {
  if (document.exitFullscreen) {
    return document.exitFullscreen();
  }
  const d = document as Document & {
    webkitExitFullscreen?: () => void;
    msExitFullscreen?: () => void;
  };
  if (d.webkitExitFullscreen) {
    d.webkitExitFullscreen();
    return Promise.resolve();
  }
  if (d.msExitFullscreen) {
    d.msExitFullscreen();
    return Promise.resolve();
  }
  return Promise.reject(new Error('Exit fullscreen not available'));
}

export default function ProjectAnalysisAdminReportPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const { alert, showAlert, handleCloseAlert } = useAlert();
  const { token } = useAuth();

  const isCreate = reportId === 'new';

  const [loadingReport, setLoadingReport] = useState(() => !isCreate);
  const [report, setReport] = useState<ProjectAnalysisReportAdminDetail | null>(null);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [userSearchInput, setUserSearchInput] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [draftTitle, setDraftTitle] = useState('');
  const [htmlFile, setHtmlFile] = useState<File | null>(null);
  const [htmlUploadedLabel, setHtmlUploadedLabel] = useState<string | null>(null);
  const [loadingSave, setLoadingSave] = useState(false);
  const [loadingPublish, setLoadingPublish] = useState(false);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  const [recipientProfileCache, setRecipientProfileCache] = useState<Record<string, UserProfile>>(
    {}
  );
  const previewFullscreenRef = useRef<HTMLDivElement>(null);
  const [isPreviewFullscreen, setIsPreviewFullscreen] = useState(false);

  useEffect(() => {
    const syncFullscreen = () => {
      setIsPreviewFullscreen(document.fullscreenElement === previewFullscreenRef.current);
    };
    document.addEventListener('fullscreenchange', syncFullscreen);
    return () => document.removeEventListener('fullscreenchange', syncFullscreen);
  }, []);

  const togglePreviewFullscreen = async () => {
    const el = previewFullscreenRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement === el) {
        await exitFullscreenDocument();
      } else {
        await requestFullscreenElement(el);
      }
    } catch {
      /* browser may block without user gesture or API missing */
    }
  };

  useEffect(() => {
    setPageIndex(0);
    setRecipientProfileCache({});
    setUserSearchInput('');
    setDebouncedUserSearch('');
  }, [reportId]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedUserSearch(userSearchInput.trim()), 400);
    return () => window.clearTimeout(id);
  }, [userSearchInput]);

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedUserSearch]);

  useEffect(() => {
    setRecipientProfileCache((prev) => {
      const next = { ...prev };
      for (const u of users) {
        if (!u.disabled) {
          next[u.id] = u;
        }
      }
      return next;
    });
  }, [users]);

  const loadReportDetail = useCallback(async () => {
    if (!reportId || isCreate) {
      if (isCreate) {
        setLoadingReport(false);
        setReport(null);
      }
      return;
    }
    setLoadingReport(true);
    try {
      const { data } = await apolloClient.query<{
        projectAnalysisReportForAdmin: ProjectAnalysisReportAdminDetail | null;
      }>({
        query: QUERY_PROJECT_ANALYSIS_REPORT_FOR_ADMIN,
        variables: { report_id: reportId },
        fetchPolicy: 'network-only',
      });
      const d = data.projectAnalysisReportForAdmin;
      if (!d) {
        setReport(null);
        setSelectedIds(new Set());
        setHtmlUploadedLabel(null);
        setRecipientProfileCache({});
        return;
      }
      setReport(d);
      const recipientIds = d.recipient_user_ids ?? [];
      setSelectedIds(new Set(recipientIds));
      setHtmlUploadedLabel(d.original_filename ?? null);

      if (recipientIds.length > 0) {
        void (async () => {
          try {
            const { data: ud } = await apolloClient.query<{ userProfiles: UserProfile[] }>({
              query: QUERY_USER_PROFILES,
              fetchPolicy: 'network-only',
            });
            const want = new Set(recipientIds);
            const patch: Record<string, UserProfile> = {};
            for (const u of ud.userProfiles) {
              if (want.has(u.id) && !u.disabled) {
                patch[u.id] = u;
              }
            }
            setRecipientProfileCache((prev) => ({ ...prev, ...patch }));
          } catch {
            /* labels optional */
          }
        })();
      }
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
      setReport(null);
    } finally {
      setLoadingReport(false);
      setPreviewRefreshKey((k) => k + 1);
    }
  }, [apolloClient, reportId, isCreate, showAlert]);

  const loadUsersPage = useCallback(async () => {
    if (!reportId) return;
    setLoadingUsers(true);
    try {
      const { data } = await apolloClient.query<UserProfilesPageResponse>({
        query: QUERY_USER_PROFILES_PAGE,
        variables: {
          limit: pageSize,
          offset: pageIndex * pageSize,
          search: debouncedUserSearch,
        },
        fetchPolicy: 'network-only',
      });
      setUsers(data.userProfiles.filter((u) => !u.disabled));
      setUsersTotal(data.userProfilesTotal);
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingUsers(false);
    }
  }, [apolloClient, reportId, pageIndex, pageSize, debouncedUserSearch, showAlert]);

  useEffect(() => {
    void loadReportDetail();
  }, [loadReportDetail]);

  useEffect(() => {
    void loadUsersPage();
  }, [loadUsersPage]);

  const refreshAfterMutation = useCallback(async () => {
    await Promise.all([loadReportDetail(), loadUsersPage()]);
  }, [loadReportDetail, loadUsersPage]);

  const pageCount = Math.max(1, Math.ceil(usersTotal / pageSize) || 1);
  const rangeStart = usersTotal === 0 ? 0 : pageIndex * pageSize + 1;
  const rangeEnd = Math.min((pageIndex + 1) * pageSize, usersTotal);

  const toggleUser = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDownloadHtml = async () => {
    if (!reportId || isCreate || !token) return;
    const name =
      report?.original_filename ?? htmlUploadedLabel ?? `${report?.title ?? 'relatorio'}.html`;
    try {
      await downloadProjectAnalysisReportHtmlFile(reportId, token, name);
    } catch {
      showAlert('error', 'Não foi possível baixar o HTML.');
    }
  };

  const handleSave = async () => {
    if (!reportId) return;

    if (isCreate) {
      if (!draftTitle.trim()) {
        showAlert('error', 'Informe o título do relatório.');
        return;
      }
      if (!token) {
        showAlert('error', 'Sessão inválida. Faça login novamente.');
        return;
      }
      setLoadingSave(true);
      try {
        const { data } = await apolloClient.mutate<{
          createProjectAnalysisReportDraft: { id: string };
        }>({
          mutation: MUTATION_CREATE_PROJECT_ANALYSIS_REPORT_DRAFT,
          variables: { title: draftTitle.trim() },
        });
        const id = data?.createProjectAnalysisReportDraft?.id;
        if (!id) {
          showAlert('error', 'Não foi possível criar o rascunho.');
          return;
        }
        if (htmlFile) {
          await uploadProjectAnalysisReportHtmlMultipart(id, htmlFile, token);
          setHtmlUploadedLabel(htmlFile.name);
          setHtmlFile(null);
        }
        await apolloClient.mutate({
          mutation: MUTATION_SET_PROJECT_ANALYSIS_REPORT_RECIPIENTS,
          variables: {
            report_id: id,
            user_ids: Array.from(selectedIds),
          },
        });
        showAlert('success', 'Rascunho salvo');
        navigate(`${AppRouters.projectAnalysisAdmin}/${id}`, { replace: true });
      } catch (error) {
        error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
      } finally {
        setLoadingSave(false);
      }
      return;
    }

    if (!report || report.status === 'ARCHIVED') return;
    setLoadingSave(true);
    try {
      if (htmlFile) {
        if (!token) {
          showAlert('error', 'Sessão inválida. Faça login novamente.');
          return;
        }
        await uploadProjectAnalysisReportHtmlMultipart(reportId, htmlFile, token);
        setHtmlUploadedLabel(htmlFile.name);
        setHtmlFile(null);
      }
      await apolloClient.mutate({
        mutation: MUTATION_SET_PROJECT_ANALYSIS_REPORT_RECIPIENTS,
        variables: {
          report_id: reportId,
          user_ids: Array.from(selectedIds),
        },
      });
      showAlert('success', report.status === 'PUBLISHED' ? 'Alterações salvas' : 'Rascunho salvo');
      await refreshAfterMutation();
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingSave(false);
    }
  };

  const handleCancel = () => {
    navigate(AppRouters.projectAnalysisAdmin);
  };

  const handlePublish = async () => {
    if (!reportId || isCreate) return;
    setLoadingPublish(true);
    try {
      await apolloClient.mutate({
        mutation: MUTATION_PUBLISH_PROJECT_ANALYSIS_REPORT,
        variables: { report_id: reportId },
      });
      showAlert('success', 'Relatório publicado');
      await refreshAfterMutation();
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingPublish(false);
    }
  };

  if (!reportId) {
    return null;
  }

  const canEdit = isCreate || report?.status === 'DRAFT' || report?.status === 'PUBLISHED';

  const serverHtmlName = report?.original_filename ?? htmlUploadedLabel;

  const publishHeaderButton =
    !isCreate && !loadingReport && report?.status === 'DRAFT' ? (
      <ButtonLoading
        type="button"
        label="Publicar"
        loading={loadingPublish}
        disabled={loadingPublish || loadingSave || selectedIds.size < 1}
        onClick={() => void handlePublish()}
      />
    ) : undefined;

  const layoutTitle = isCreate ? (
    <>
      <span>Novo relatório</span>
      <ReportStatusTag status="DRAFT" />
    </>
  ) : loadingReport || !report ? (
    'Relatório'
  ) : (
    <>
      <span className="truncate max-w-[min(100%,36rem)]" title={report.title}>
        {report.title}
      </span>
      <ReportStatusTag status={report.status} />
    </>
  );

  const serverPreviewId = report?.id ?? (reportId !== 'new' ? reportId : null);

  const recipientWarning = canEdit && selectedIds.size < 1;

  return (
    <PrivateAdminRoute>
      <Layout title={layoutTitle} customButton={publishHeaderButton}>
        {loadingReport ? (
          <p className="text-sm text-slate-500">Carregando…</p>
        ) : !isCreate && !report ? (
          <p className="text-sm text-slate-500">Relatório não encontrado.</p>
        ) : isCreate || report ? (
          <div className="grid grid-cols-1 gap-5 lg:gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(300px,420px)] xl:items-start">
            <div className="flex min-w-0 flex-col gap-5">
              <section className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <h2 className="text-sm font-semibold text-slate-900">Dados do relatório</h2>
                <InputWithLabel
                  label="Título"
                  id="report-title"
                  name="title"
                  value={isCreate ? draftTitle : report?.title ?? ''}
                  onChange={(e) => isCreate && setDraftTitle(e.target.value)}
                  readOnly={!isCreate}
                  disabled={!isCreate && !canEdit}
                  placeholder={isCreate ? 'Ex.: Análise trimestral Q1' : undefined}
                  className="max-w-lg"
                />
                {!isCreate && report && (
                  <div className="mt-1 flex flex-col gap-1 border-t border-slate-100 pt-3 text-sm text-slate-600">
                    {report.published_at && (
                      <p>
                        <span className="text-slate-500">Publicado:</span>{' '}
                        {dateFormat(new Date(report.published_at))}
                      </p>
                    )}
                    <p>
                      <span className="text-slate-500">Destinatários (salvos):</span>{' '}
                      {report.recipient_count}
                    </p>
                  </div>
                )}
              </section>

              <section className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="space-y-0.5">
                  <h2 className="text-sm font-semibold text-slate-900">HTML</h2>
                  <p className="text-xs text-slate-500">Arquivos .html ou .htm</p>
                </div>
                {!canEdit && (
                  <p className="text-sm text-slate-600 rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
                    Relatórios arquivados não podem ser editados.
                  </p>
                )}
                <FileUpload
                  name="html"
                  label="Arquivo HTML"
                  accept=".html,.htm"
                  value={htmlFile}
                  onChange={setHtmlFile}
                  disabled={!canEdit}
                  existingFileLabel={htmlFile ? null : serverHtmlName}
                  existingHint={
                    serverHtmlName && !htmlFile ? 'Arquivo atual no servidor' : undefined
                  }
                  onDownloadExisting={
                    !isCreate && serverHtmlName && token
                      ? () => void handleDownloadHtml()
                      : undefined
                  }
                  downloadExistingLabel="Baixar"
                  replaceFileLabel="Substituir arquivo"
                />
              </section>

              <section className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
                <div className="space-y-1">
                  <h2 className="text-sm font-semibold text-slate-900">Destinatários</h2>
                  {canEdit && (
                    <p className="text-xs text-slate-500">
                      A lista selecionada substitui a anterior. Selecionados agora:{' '}
                      <span className="font-medium text-slate-700">{selectedIds.size}</span>
                    </p>
                  )}
                </div>
                {selectedIds.size > 0 && (
                  <div
                    className={cn(
                      'rounded-lg border px-3 py-2.5',
                      canEdit ? 'border-primary/35 bg-primary/5' : 'border-slate-200 bg-slate-50'
                    )}
                  >
                    <p
                      className={cn(
                        'mb-2 text-xs font-medium',
                        canEdit ? 'text-primary' : 'text-slate-600'
                      )}
                    >
                      {canEdit ? 'Seleção atual' : 'Destinatários'}{' '}
                      <span className="tabular-nums">({selectedIds.size})</span>
                    </p>
                    <ul className="flex flex-wrap gap-2">
                      {Array.from(selectedIds)
                        .sort()
                        .map((id) => {
                          const p = recipientProfileCache[id];
                          const label =
                            p?.display_name?.trim() || p?.email || `ID …${id.slice(-8)}`;
                          return (
                            <li
                              key={id}
                              className={cn(
                                'inline-flex max-w-[min(100%,280px)] items-center gap-0.5 rounded-full border text-xs font-medium shadow-sm',
                                canEdit
                                  ? 'border-primary/40 bg-white pl-2.5 text-slate-800 pr-0.5'
                                  : 'border-slate-200 bg-white px-2.5 py-1 text-slate-700'
                              )}
                            >
                              <span className="truncate py-1" title={p?.email ?? id}>
                                {label}
                              </span>
                              {canEdit && (
                                <button
                                  type="button"
                                  className="rounded-full p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                                  aria-label={`Remover ${label}`}
                                  onClick={() => toggleUser(id)}
                                >
                                  <X className="h-3.5 w-3.5 shrink-0" />
                                </button>
                              )}
                            </li>
                          );
                        })}
                    </ul>
                  </div>
                )}
                {canEdit ? (
                  <>
                    <Input
                      placeholder="Buscar usuários (e-mail ou nome)"
                      value={userSearchInput}
                      onChange={(e) => setUserSearchInput(e.target.value)}
                      className="max-w-md"
                      autoComplete="off"
                    />
                    <div className="flex max-h-52 min-h-[112px] flex-col gap-1 overflow-y-auto rounded-md border border-slate-200 bg-slate-50/50 p-2">
                      {loadingUsers
                        ? Array.from({ length: pageSize }).map((_, i) => (
                            <Skeleton key={i} className="h-10 w-full shrink-0" />
                          ))
                        : users.map((u) => {
                            const isSelected = selectedIds.has(u.id);
                            return (
                              <label
                                key={u.id}
                                className={cn(
                                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm cursor-pointer transition-colors border',
                                  isSelected
                                    ? 'bg-primary/10 border-primary/40 text-slate-900 shadow-sm'
                                    : 'border-transparent bg-slate-50/80 hover:bg-slate-100/90 text-slate-700'
                                )}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleUser(u.id)}
                                />
                                <span className={cn(isSelected && 'font-medium')}>
                                  {u.display_name || u.email} ({u.email})
                                </span>
                              </label>
                            );
                          })}
                    </div>
                    <div className="flex flex-col gap-2 border-t border-slate-100 pt-3 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                      <p className="text-xs tabular-nums text-slate-500">
                        {usersTotal === 0
                          ? 'Nenhum usuário'
                          : `Mostrando ${rangeStart}–${rangeEnd} de ${usersTotal}`}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                        <Select
                          value={String(pageSize)}
                          onValueChange={(v) => {
                            setPageSize(Number(v));
                            setPageIndex(0);
                          }}
                        >
                          <SelectTrigger className="h-8 w-[130px]">
                            <SelectValue placeholder={pageSize} />
                          </SelectTrigger>
                          <SelectContent>
                            {[10, 15, 20, 30, 50].map((n) => (
                              <SelectItem key={n} value={String(n)}>
                                {n} por página
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            disabled={pageIndex === 0 || loadingUsers}
                            onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="min-w-[100px] text-center text-xs text-slate-600">
                            Página {pageIndex + 1} / {pageCount}
                          </span>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            disabled={
                              pageIndex >= pageCount - 1 || loadingUsers || usersTotal === 0
                            }
                            onClick={() => setPageIndex((p) => p + 1)}
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-600 rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
                    {report?.recipient_count ?? 0} destinatário(s). Relatórios arquivados não podem
                    ser editados.
                  </p>
                )}
              </section>

              <div className="flex flex-col gap-2 rounded-lg border border-slate-200 bg-slate-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  {recipientWarning && (
                    <p className="text-xs text-amber-800">
                      {report?.status === 'DRAFT' || isCreate
                        ? 'Adicione pelo menos um destinatário e envie o HTML antes de publicar.'
                        : 'Adicione pelo menos um destinatário antes de salvar.'}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                  <ButtonLoading
                    type="button"
                    label="Salvar"
                    loading={loadingSave}
                    disabled={!canEdit || loadingSave || loadingPublish}
                    variant="secondary"
                    onClick={() => void handleSave()}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancel}
                    disabled={loadingSave || loadingPublish}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            </div>

            <aside className="flex min-w-0 flex-col xl:sticky xl:top-4 xl:max-h-[calc(100vh-6rem)]">
              <div
                ref={previewFullscreenRef}
                className={cn(
                  'flex min-w-0 flex-1 flex-col gap-2 overflow-hidden rounded-lg border border-slate-200 bg-slate-50/40 p-3 shadow-sm sm:p-4',
                  isPreviewFullscreen ? 'min-h-0 bg-white' : 'min-h-[min(420px,50vh)]'
                )}
              >
                <div className="flex shrink-0 items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold text-slate-900">Pré-visualização</h2>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => void togglePreviewFullscreen()}
                    aria-label={
                      isPreviewFullscreen
                        ? 'Sair da tela cheia'
                        : 'Ver pré-visualização em tela cheia'
                    }
                    title={isPreviewFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                  >
                    {isPreviewFullscreen ? (
                      <Minimize2 className="h-4 w-4" />
                    ) : (
                      <Maximize2 className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <div className="min-h-0 flex-1 overflow-hidden rounded-md border border-slate-200/80 bg-white">
                  <ReportHtmlPreview
                    htmlFile={htmlFile}
                    serverReportId={serverPreviewId}
                    token={token ?? null}
                    refreshKey={previewRefreshKey}
                    frameClassName={
                      isPreviewFullscreen ? 'h-full min-h-0 border-0 rounded-md' : undefined
                    }
                  />
                </div>
              </div>
            </aside>
          </div>
        ) : null}
      </Layout>

      <Alert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
      />
    </PrivateAdminRoute>
  );
}
